import { $curSandTable, $sceneMap, $tourDataApi } from '@stores/dataStores';
import {
  $editingMapMarker,
  $editingMapMarkerApi,
} from '@stores/editingMarkerStores';
import { $playerWidgetsApi } from '@stores/playerWidgets';
import {
  type GeneralID,
  type STMapMarker,
  STMapMarkerType,
  SandTableType,
} from '@types';
import { useStore } from 'effector-react';
import { useEffect, useState } from 'react';
import useConstant from 'use-constant';
import PanelBase from '../../panel/PanelBase';
import { type AMapObjectType, STAMap, stEditorAmap } from '../STAMap';
import { showBatchAddTip } from '../components/BatchAddTip';
import { getSceneMapLocation } from '../utils/getSceneMapLocation';

import { isMapFeatureMarker } from '../utils/markerTypeCheck';
import { newMarkerId } from '../utils/newMarkerId';
import { MarkerSettingsFeature } from './MarkerSettingsFeature';
import { MarkerSettingsPulse } from './MarkerSettingsPulse';
import { MarkerSettingsScene } from './MarkerSettingsScene';

interface Props {
  sandTableId: string;
}
type AMapObjectTypeFixed = AMapObjectType & {
  setzIndex?: (zIndex: number) => void;
};

export function PanelSandTableMap({ sandTableId }: Props) {
  const { marker, isNew, batchMode = false } = useStore($editingMapMarker)!;
  const sceneMap = useStore($sceneMap);
  const firstState = useConstant(() => marker);
  const [idList, setIdList] = useState<GeneralID[]>([]);

  useEffect(() => {
    if (batchMode) return;

    const markerObject = stEditorAmap.getMarkerObjectById(
      marker.id
    ) as AMapObjectTypeFixed | null;
    markerObject?.setzIndex?.(STAMap.editingZIndex);

    stEditorAmap.setEditingId(marker.id);
    return () => {
      stEditorAmap.hideRadar();
      stEditorAmap.setRadarFlag(false);
      stEditorAmap.setEditingId(undefined);
    };
  }, []);

  useEffect(() => {
    return () => {
      $editingMapMarkerApi.clear();
      $playerWidgetsApi.clear();
    };
  }, []);

  const handleClose = () => {
    $editingMapMarkerApi.clear();
    const sandTable = $curSandTable.getState();
    if (!sandTable || sandTable.type !== SandTableType.map) return;
    const markerData = sandTable.markers.find((x) => x.id === marker.id);
    if (markerData) {
      stEditorAmap.addMarker(markerData);
    } else {
      stEditorAmap.removeMarker(marker.id);
    }
  };

  const handleDelete = () => {
    if (!isNew) {
      $tourDataApi.updateSandtable({
        id: sandTableId,
        updater: (draft) => {
          const list = draft.markers as STMapMarker[];
          draft.markers = list.filter((x) => x.id !== marker.id);
        },
      });
    }
    stEditorAmap.removeMarker(marker.id);
  };

  const handleConfirm = async () => {
    if (batchMode) {
      if (!idList.length) return;
      const pos = stEditorAmap.getCenter();
      if (!pos) return;
      let hasGpsMaker = false;
      const newMarkers: STMapMarker[] = await Promise.all(
        idList.map(async (_id) => {
          const sceneData = sceneMap.get(_id)!;
          let { lat, lng } = pos;
          const mapLocation = await getSceneMapLocation(sceneData);
          if (mapLocation) {
            hasGpsMaker = true;
            lat = mapLocation.lat;
            lng = mapLocation.lng;
          }
          // 目前批量设置只有场景标记
          if (marker.type === STMapMarkerType.scene) {
            return {
              ...marker,
              id: newMarkerId(),
              title: sceneData.name,
              icon: { ...marker.icon, lat, lng },
              data: {
                type: 0,
                sceneType: sceneData.type,
                sceneId: sceneData.id,
              },
            };
          }
          return marker;
        })
      );
      $tourDataApi.updateSandtable({
        id: sandTableId,
        updater: (draft) => {
          const list = draft.markers as STMapMarker[];
          draft.markers = [...list, ...newMarkers];
        },
      });
      newMarkers.forEach((x) => {
        stEditorAmap.addMarker(x);
      });
      if (hasGpsMaker) {
        stEditorAmap.autoFit();
      }
      showBatchAddTip();
    } else {
      $tourDataApi.updateSandtable({
        id: sandTableId,
        updater: (draft) => {
          const list = draft.markers as STMapMarker[];
          const index = list.findIndex((x) => x.id === marker.id);
          if (index > -1) {
            list[index] = marker;
          } else {
            list.push(marker);
          }
        },
      });
    }
  };

  const renderContent = () => {
    if (marker.type === undefined || marker.type === STMapMarkerType.scene) {
      return (
        <MarkerSettingsScene
          marker={marker}
          batchMode={batchMode}
          idList={idList}
          onIdListChange={setIdList}
        />
      );
    }
    if (marker.type === STMapMarkerType.pulse) {
      return <MarkerSettingsPulse marker={marker} isNew={!!isNew} />;
    }
    if (isMapFeatureMarker(marker)) {
      return <MarkerSettingsFeature marker={marker} isNew={!!isNew} />;
    }
  };

  return (
    <PanelBase
      title="添加标记"
      isChanged={() => firstState !== marker}
      onConfirm={handleConfirm}
      onRequestClose={handleClose}
      onDeleteClick={batchMode ? undefined : handleDelete}
    >
      {renderContent()}
    </PanelBase>
  );
}
